import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ChevronRightIcon, EllipsisHorizontalIcon } from '@heroicons/react/20/solid';
import { bookingApi, getTemplates, linkGuardianDirect, removeGuardian } from '../../../utils/bookingApi';
import { formatDate, formatMonth } from '../../../utils/dates';
import GenderSelect from '../../../components/GenderSelect';
import { useAuth } from '../../../contexts/AuthContext';
import { MessageFamilyButton } from '../../../components/chat/MessageButtons';
import { useFeatures } from '../../../hooks/useFeature';
import { askAboutExistingRecord } from '../../../utils/existingRecord';
import { waLink, roleLabel } from './memberShared';
import StaffRoles from '../../../components/members/StaffRoles';
import {
  PageHeader, Tabs, useUrlTab, Chevron, Sheet, Picker, Skeleton, ErrorState, useConfirm, useToast,
} from '../../../components/ui';
import '../booking-shared.css';
import './AdminMemberProfile.css';
import { archiveCreditNote } from '../../../utils/archiveCredit';
import { typeName } from '../../../utils/sessionTypes';
import SessionTypeApprovals from '../../../components/sessions/SessionTypeApprovals';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const money = (pence) => `£${(pence / 100).toFixed(2)}`;
const errorText = (err, fallback) => err?.response?.data?.error || fallback;

// A pop-up with one small form. onSave() returns an error message to show,
// or nothing when it worked (the caller closes it).
function FormSheet({ title, saveLabel = 'Save', savingLabel = 'Saving…', dirty, onSave, onClose, wide, children, danger }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    setError(null);
    setSaving(true);
    const problem = await onSave();
    if (problem) { setError(problem); setSaving(false); }
  };
  return (
    <Sheet
      title={title}
      wide={wide}
      onClose={onClose}
      dirty={dirty}
      onSubmit={submit}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" className={`bk-btn ${danger ? 'bk-btn--danger' : 'bk-btn--primary'}`} disabled={saving}>
            {saving ? savingLabel : saveLabel}
          </button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      <div className="mp-form">
        {children}
        {error && <p className="bk-form-error" role="alert">{error}</p>}
      </div>
    </Sheet>
  );
}

function AssignCreditSheet({ userId, onDone, onClose }) {
  const [form, setForm] = useState({ amount: '', expiresInDays: '90', note: '' });
  const dirty = form.amount !== '' || form.note !== '' || form.expiresInDays !== '90';
  const save = async () => {
    const amount = Math.round(parseFloat(form.amount) * 100);
    if (!Number.isFinite(amount) || amount < 1) return 'Enter an amount above £0.';
    const days = parseInt(form.expiresInDays, 10);
    if (!Number.isInteger(days) || days < 1) return 'Enter how many days it lasts, 1 or more.';
    try {
      await bookingApi.assignCredit({ userId, amount, expiresInDays: days, note: form.note || undefined });
    } catch (err) {
      return errorText(err, "Couldn't assign the credit.");
    }
    onDone(amount);
    return null;
  };
  return (
    <FormSheet title="Assign credit" saveLabel="Assign credit" savingLabel="Assigning…" dirty={dirty} onSave={save} onClose={onClose}>
      <div className="bk-grid-2">
        <label className="bk-label">Amount (£)
          <input type="number" step="0.01" min="0.01" className="bk-input" inputMode="decimal" required
            value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
        </label>
        <label className="bk-label">Expires after (days)
          <input type="number" min="1" className="bk-input" inputMode="numeric" required
            value={form.expiresInDays} onChange={e => setForm(f => ({ ...f, expiresInDays: e.target.value }))} />
        </label>
      </div>
      <label className="bk-label">Note <span className="bk-optional">(optional)</span>
        <input type="text" maxLength={200} className="bk-input" placeholder="For example, missed session refund"
          value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
      </label>
    </FormSheet>
  );
}

const inAWeek = () => { const d = new Date(); d.setDate(d.getDate() + 7); return d.toISOString().split('T')[0]; };

function AddChargeSheet({ userId, onDone, onClose }) {
  const [form, setForm] = useState(() => ({ description: '', amount: '', dueDate: inAWeek() }));
  const dirty = form.description !== '' || form.amount !== '';
  const save = async () => {
    if (!form.description.trim()) return 'Enter what the charge is for.';
    const amountPence = Math.round(parseFloat(form.amount) * 100);
    if (Number.isNaN(amountPence) || amountPence < 1) return 'Enter an amount above £0.';
    if (!form.dueDate) return 'Choose a due date.';
    try {
      await bookingApi.createCharge({
        userId,
        amount: amountPence,
        description: form.description,
        dueDate: new Date(form.dueDate).toISOString(),
      });
    } catch (err) {
      return errorText(err, "Couldn't add the charge.");
    }
    onDone();
    return null;
  };
  return (
    <FormSheet title="Add a charge" saveLabel="Add charge" savingLabel="Adding…" dirty={dirty} onSave={save} onClose={onClose}>
      <label className="bk-label">Description
        <input className="bk-input" required value={form.description} placeholder="For example, club hoodie"
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      </label>
      <div className="bk-grid-2">
        <label className="bk-label">Amount (£)
          <input type="number" step="0.01" min="0.01" className="bk-input" inputMode="decimal" required value={form.amount}
            onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
        </label>
        <label className="bk-label">Due date
          <input type="date" className="bk-input" required value={form.dueDate}
            onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} />
        </label>
      </div>
    </FormSheet>
  );
}

function EditProfileSheet({ member, onDone, onClose }) {
  const start = { firstName: member.firstName, lastName: member.lastName, email: member.email, phone: member.phone || '' };
  const [form, setForm] = useState(start);
  const dirty = Object.keys(start).some(k => form[k] !== start[k]);
  const save = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) return 'Enter their first and last name.';
    if (!form.email.trim()) return 'Enter their email.';
    try {
      await bookingApi.updateMemberProfile(member.id, form);
    } catch (err) {
      return errorText(err, "Couldn't save their details.");
    }
    onDone();
    return null;
  };
  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));
  return (
    <FormSheet title="Edit details" dirty={dirty} onSave={save} onClose={onClose}>
      <div className="bk-grid-2">
        <label className="bk-label">First name<input className="bk-input" value={form.firstName} onChange={set('firstName')} required /></label>
        <label className="bk-label">Last name<input className="bk-input" value={form.lastName} onChange={set('lastName')} required /></label>
      </div>
      <label className="bk-label">Email<input type="email" className="bk-input" value={form.email} onChange={set('email')} required /></label>
      <label className="bk-label">Phone number <span className="bk-optional">(optional)</span>
        <input type="tel" className="bk-input" value={form.phone} onChange={set('phone')} />
      </label>
    </FormSheet>
  );
}

const EMPTY_CHILD = { firstName: '', lastName: '', dateOfBirth: '', healthNotes: '', healthNotesNone: false };

function AddChildSheet({ userId, onDone, onClose }) {
  const [form, setForm] = useState(EMPTY_CHILD);
  const dirty = Object.keys(EMPTY_CHILD).some(k => form[k] !== EMPTY_CHILD[k]);
  const save = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) return 'Enter their first and last name.';
    if (!form.dateOfBirth) return 'Enter their date of birth.';
    if (!form.healthNotesNone && !form.healthNotes.trim()) {
      return 'Describe any health issues or learning differences, or tick that there are none.';
    }
    try {
      await bookingApi.adminAddChild({
        userId,
        firstName: form.firstName,
        lastName: form.lastName,
        dateOfBirth: form.dateOfBirth,
        healthNotes: form.healthNotesNone ? 'none' : form.healthNotes.trim(),
      });
    } catch (err) {
      return errorText(err, "Couldn't add the child.");
    }
    onDone(`${form.firstName} ${form.lastName}`);
    return null;
  };
  return (
    <FormSheet title="Add a child" saveLabel="Add child" savingLabel="Adding…" dirty={dirty} onSave={save} onClose={onClose}>
      <div className="bk-grid-2">
        <label className="bk-label">First name
          <input className="bk-input" value={form.firstName} required onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
        </label>
        <label className="bk-label">Last name
          <input className="bk-input" value={form.lastName} required onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
        </label>
      </div>
      <label className="bk-label">Date of birth
        <input type="date" className="bk-input" value={form.dateOfBirth} required onChange={e => setForm(f => ({ ...f, dateOfBirth: e.target.value }))} />
      </label>
      <label className="mp-check">
        <input type="checkbox" className="bk-checkbox" checked={form.healthNotesNone}
          onChange={e => setForm(f => ({ ...f, healthNotesNone: e.target.checked }))} />
        No known health issues or learning differences
      </label>
      <label className="bk-label">Health issues or learning differences
        <textarea className="bk-input" rows={2} value={form.healthNotes} disabled={form.healthNotesNone}
          placeholder="Describe any conditions, or tick none above"
          onChange={e => setForm(f => ({ ...f, healthNotes: e.target.value }))} />
      </label>
    </FormSheet>
  );
}

// Gymnast logins can't hold staff roles, so they keep this dropdown (for
// example to make one an adult member; nobody can be made a gymnast login, as
// gymnasts sign in with the club's code). Everyone else has role tick boxes.
const ASSIGNABLE_ROLES = [
  { value: 'CLUB_ADMIN', label: 'Admin' },
  { value: 'COACH', label: 'Coach' },
  { value: 'WELFARE', label: 'Welfare' },
  { value: 'ADULT', label: 'Adult' },
];

function RoleSelector({ member, onDone, onCancel }) {
  const confirm = useConfirm();
  const [role, setRole] = useState(member.role);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    if (role === member.role) { onCancel(); return; }
    if (role === 'CLUB_ADMIN') {
      const ok = await confirm({
        title: 'Make them a Club admin?',
        message: 'Club admin gives full access to everything in the club.',
        confirmLabel: 'Make admin',
        tone: 'primary',
      });
      if (!ok) return;
    }
    setSaving(true);
    setError(null);
    try {
      await bookingApi.changeRole(member.id, role);
      onDone();
    } catch (err) {
      setError(errorText(err, "Couldn't change the role."));
      setSaving(false);
    }
  };

  return (
    <div className="mp-form">
      <label className="bk-label">Role
        <select className="bk-select" value={role} onChange={e => setRole(e.target.value)}>
          {ASSIGNABLE_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </label>
      {error && <p className="bk-form-error" role="alert">{error}</p>}
      <div className="bk-actions">
        <button type="button" className="bk-btn bk-btn--primary" disabled={saving || role === member.role} onClick={handleSave}>
          {saving ? 'Saving…' : 'Save role'}
        </button>
        <button type="button" className="bk-btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// An adult's own participant record: unlinking it from their account, with
// the choice to archive it too (Club admins)
function UnlinkSelfSheet({ gymnast: g, onDone, onClose }) {
  const { isClubAdmin: canArchive } = useAuth();
  const [archive, setArchive] = useState(true);
  const save = async () => {
    let res;
    try {
      res = await bookingApi.unlinkGymnastUser(g.id, canArchive && archive ? { archive: true } : {});
    } catch (err) {
      return errorText(err, "Couldn't unlink them.");
    }
    // Paid future bookings cancelled by the archive were credited: say so
    onDone(archiveCreditNote(res?.data?.cascade));
    return null;
  };
  return (
    <FormSheet title={`Unlink ${g.firstName} ${g.lastName}?`} saveLabel="Unlink" savingLabel="Unlinking…" danger onSave={save} onClose={onClose}>
      <p className="mp-flush">
        The link between this account and the {g.firstName} {g.lastName} participant record is removed.
        {!(canArchive && archive) && ' The record and its history stay, unlinked from any account.'}
      </p>
      {canArchive && (
        <label className="mp-check">
          <input type="checkbox" checked={archive} onChange={e => setArchive(e.target.checked)} />
          <span>Also archive the record, so it doesn't stay behind as a duplicate. Its history is kept and it can be restored. Its future bookings are cancelled, and paid ones are credited to the family.</span>
        </label>
      )}
    </FormSheet>
  );
}

function LinkAccountSheet({ gymnast: g, allUsers, onDone, onClose }) {
  const [userId, setUserId] = useState('');
  const items = useMemo(() => {
    const linked = new Set((g.guardians || []).map(gu => gu.id));
    return (allUsers || []).filter(u => !linked.has(u.id)).map(u => ({
      id: u.id, label: `${u.firstName} ${u.lastName}`, detail: u.email || undefined, search: u.email || '',
    }));
  }, [g.guardians, allUsers]);
  const save = async () => {
    if (!userId) return 'Choose an account to link.';
    try {
      await linkGuardianDirect(g.id, userId);
    } catch (err) {
      return errorText(err, "Couldn't link the account.");
    }
    onDone();
    return null;
  };
  return (
    <FormSheet title={`Link an account to ${g.firstName}`} saveLabel="Link account" savingLabel="Linking…" dirty={!!userId} onSave={save} onClose={onClose}>
      <div className="bk-label">
        <label htmlFor={`link-${g.id}`}>Account</label>
        <Picker id={`link-${g.id}`} label="Account" items={items} value={userId} onChange={setUserId}
          placeholder="Search by name or email…" noneText="No matching accounts." />
      </div>
    </FormSheet>
  );
}

function bgExpiry(g) {
  const graceDays = g.bgNumberGraceDays ?? 14;
  const expiredDays = g.bgNumberExpiredAt
    ? Math.floor((Date.now() - new Date(g.bgNumberExpiredAt)) / (24 * 60 * 60 * 1000))
    : null;
  const remaining = expiredDays != null ? Math.max(0, graceDays - expiredDays) : null;
  return { remaining, blocked: remaining === 0 };
}

// Kept as style objects: SessionTypeApprovals lays out its rows with them
const INFO_ITEM_STYLE = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
  padding: '0.25rem 0', borderBottom: '1px solid rgba(0,0,0,0.05)',
  gap: '0.75rem', fontSize: '0.82rem',
};
const KEY_STYLE = { color: 'var(--booking-text-muted)', flexShrink: 0 };

// One participant, inside their card on the member profile. Read-only until
// Edit is pressed; each change opens its own small pop-up.
function GymnastRow({ g, memberships, templates, onUpdated, allUsers, profileUserId }) {
  const membership = memberships.find(m => m.gymnastId === g.id && m.status !== 'CANCELLED') ?? null;
  const has = useFeatures();
  const bgOn = has('bgNumbers');
  const confirm = useConfirm();
  const toast = useToast();

  const [editMode, setEditMode] = useState(false);
  const [editing, setEditing] = useState(null); // 'name' | 'dob' | 'bg' | 'health' | 'link' | 'unlink'
  const [nameForm, setNameForm] = useState({ firstName: g.firstName, lastName: g.lastName });
  const [dobValue, setDobValue] = useState('');
  const [bgInput, setBgInput] = useState(g.bgNumber || '');
  const [healthNotesValue, setHealthNotesValue] = useState(g.healthNotes === 'none' ? '' : g.healthNotes || '');
  const [healthNotesNone, setHealthNotesNone] = useState(g.healthNotes === 'none');
  const [bgSaving, setBgSaving] = useState(false);
  const [bgError, setBgError] = useState(null);
  const [error, setError] = useState(null);
  const [removingGuardianId, setRemovingGuardianId] = useState(null);

  const [commitments, setCommitments] = useState(null);
  const [commitmentLoading, setCommitmentLoading] = useState(false);
  const [commitmentError, setCommitmentError] = useState(null);
  const [addingTemplateId, setAddingTemplateId] = useState('');
  const defaultStartDate =
    membership?.status === 'SCHEDULED' && membership?.startDate
      ? new Date(membership.startDate).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
  const [addingStartDate, setAddingStartDate] = useState(defaultStartDate);

  const close = () => setEditing(null);
  // Runs a change from one of the pop-ups; returns an error message or nothing
  const saveWith = async (fn, fallback) => {
    try {
      await fn();
    } catch (err) {
      return errorText(err, fallback);
    }
    setEditing(null);
    onUpdated();
    return null;
  };

  const saveName = () => {
    if (!nameForm.firstName.trim() || !nameForm.lastName.trim()) return Promise.resolve('Enter their first and last name.');
    return saveWith(() => bookingApi.updateGymnast(g.id, {
      firstName: nameForm.firstName.trim(),
      lastName: nameForm.lastName.trim(),
      dateOfBirth: g.dateOfBirth ? new Date(g.dateOfBirth).toISOString().slice(0, 10) : undefined,
    }), "Couldn't save the name.");
  };

  const saveDob = () => {
    if (!dobValue) return Promise.resolve('Enter their date of birth.');
    return saveWith(() => bookingApi.updateGymnast(g.id, {
      firstName: g.firstName, lastName: g.lastName, dateOfBirth: dobValue,
    }), "Couldn't save the date of birth.");
  };

  const saveBgNumber = () => {
    if (!bgInput.trim()) return Promise.resolve('Enter their BG number.');
    return saveWith(() => bookingApi.setBgNumber(g.id, bgInput.trim()), "Couldn't save the BG number.");
  };

  const saveHealthNotes = () => saveWith(
    () => bookingApi.updateHealthNotes(g.id, { healthNotes: healthNotesNone ? 'none' : healthNotesValue || null }),
    "Couldn't save the health notes.",
  );

  const handleVerifyBgNumber = async (action) => {
    setBgSaving(true);
    setBgError(null);
    try {
      await bookingApi.verifyBgNumber(g.id, action);
      onUpdated();
    } catch (err) {
      setBgError(errorText(err, "Couldn't update the BG number."));
    } finally {
      setBgSaving(false);
    }
  };

  const loadCommitments = async () => {
    setCommitmentLoading(true);
    setCommitmentError(null);
    try {
      const res = await bookingApi.getCommitmentsForGymnast(g.id);
      setCommitments(res.data);
    } catch {
      setCommitmentError("Couldn't load the weekly slots.");
    } finally {
      setCommitmentLoading(false);
    }
  };

  useEffect(() => {
    loadCommitments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddCommitment = async () => {
    if (!addingTemplateId) return;
    setCommitmentError(null);
    try {
      await bookingApi.createCommitment({ gymnastId: g.id, templateId: addingTemplateId, startDate: addingStartDate });
      setAddingTemplateId('');
      await loadCommitments();
      toast('Slot added');
    } catch (err) {
      setCommitmentError(errorText(err, "Couldn't add the slot."));
    }
  };

  const handleToggleCommitmentStatus = async (commitment) => {
    const newStatus = commitment.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    setCommitmentError(null);
    try {
      await bookingApi.updateCommitmentStatus(commitment.id, newStatus);
      await loadCommitments();
    } catch (err) {
      setCommitmentError(errorText(err, "Couldn't update the slot."));
    }
  };

  const handleDeleteCommitment = async (c, label) => {
    const ok = await confirm({
      title: `Remove ${label}?`,
      message: `${g.firstName} stops being booked into it each week.`,
      confirmLabel: 'Remove slot',
    });
    if (!ok) return;
    setCommitmentError(null);
    try {
      await bookingApi.deleteCommitment(c.id);
      await loadCommitments();
      toast('Slot removed');
    } catch (err) {
      setCommitmentError(errorText(err, "Couldn't remove the slot."));
    }
  };

  const handleRemoveGuardian = async (userId) => {
    setRemovingGuardianId(userId);
    setError(null);
    try {
      await removeGuardian(g.id, userId);
      onUpdated();
    } catch (err) {
      setError(errorText(err, "Couldn't unlink that account."));
    } finally {
      setRemovingGuardianId(null);
    }
  };

  const removeChild = async () => {
    const ok = await confirm({
      title: `Remove ${g.firstName} ${g.lastName}?`,
      message: 'This deletes all their booking history.',
      confirmLabel: 'Remove child',
    });
    if (!ok) return;
    setError(null);
    try {
      await bookingApi.deleteGymnast(g.id);
      toast(`${g.firstName} ${g.lastName} removed`);
      onUpdated();
    } catch (err) {
      setError(errorText(err, "Couldn't remove them."));
    }
  };

  const getConsentValue = (type) => g.consents?.find(c => c.type === type)?.granted;
  const consent = (type) => (getConsentValue(type)
    ? <span className="bk-pill bk-pill--good">Allowed</span>
    : <span className="bk-pill bk-pill--bad">Not allowed</span>);
  const fieldButton = (label, onClick, text = 'Edit') => (
    <button type="button" className="mp-text-btn" aria-label={label} onClick={onClick}>{text}</button>
  );

  const otherGuardians = (g.guardians || []).filter(gu => gu.id !== profileUserId);
  const editable = editMode;

  return (
    <div>
      <div className="mp-edit-bar">
        {editMode && (
          <button type="button" className="bk-btn bk-btn--sm"
            onClick={() => { setNameForm({ firstName: g.firstName, lastName: g.lastName }); setEditing('name'); }}>
            Edit name
          </button>
        )}
        <button type="button" className={`bk-btn bk-btn--sm${editMode ? ' bk-btn--primary' : ''}`}
          onClick={() => { setEditMode(v => !v); setEditing(null); }}>
          {editMode ? 'Done' : 'Edit'}
        </button>
      </div>

      {error && <p className="bk-form-error" role="alert">{error}</p>}

      <ul className="mp-info">
        <li style={INFO_ITEM_STYLE}>
          <span style={KEY_STYLE}>Date of birth</span>
          <span>
            {g.dateOfBirth
              ? <>{formatDate(g.dateOfBirth)} {editable && fieldButton('Edit date of birth', () => { setDobValue(new Date(g.dateOfBirth).toISOString().slice(0, 10)); setEditing('dob'); })}</>
              : <><span className="bk-pill bk-pill--bad">Missing</span> {editable && fieldButton('Set date of birth', () => { setDobValue(''); setEditing('dob'); }, 'Set')}</>}
          </span>
        </li>
        <li style={INFO_ITEM_STYLE}>
          <span style={KEY_STYLE}>Competes as</span>
          {editable
            ? <GenderSelect gymnastId={g.id} value={g.gender} onSaved={() => onUpdated()} />
            : <span>{g.gender === 'FEMALE' ? 'Girls' : g.gender === 'MALE' ? 'Boys' : 'Not set'}</span>}
        </li>
        <li style={INFO_ITEM_STYLE}>
          <span style={KEY_STYLE}>Coaching photos</span>
          {consent('photo_coaching')}
        </li>
        <li style={INFO_ITEM_STYLE}>
          <span style={KEY_STYLE}>Social media</span>
          {consent('photo_social_media')}
        </li>
        {bgOn && (
          <li style={{ ...INFO_ITEM_STYLE, borderBottom: 'none' }}>
            <span style={KEY_STYLE}>BG insurance</span>
            <span className="mp-info__value">
              {g.bgNumberStatus === 'VERIFIED' && (
                <>
                  <span className="bk-pill bk-pill--good">Verified</span>
                  <span className="mp-mono">{g.bgNumber}</span>
                  {editable && (
                    <button type="button" className="bk-btn bk-btn--sm" disabled={bgSaving} onClick={() => handleVerifyBgNumber('expire')}
                      title="Marks the membership expired and starts a 14-day grace; bookings continue during the grace, then pause until renewed">
                      Mark expired
                    </button>
                  )}
                </>
              )}
              {g.bgNumberStatus === 'PENDING' && (
                <>
                  <span className="bk-pill bk-pill--wait">Pending</span>
                  <span className="mp-mono">{g.bgNumber}</span>
                  {editable && <button type="button" className="bk-btn bk-btn--sm" disabled={bgSaving} onClick={() => handleVerifyBgNumber('verify')}>Verify</button>}
                  {editable && <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger" disabled={bgSaving} onClick={() => handleVerifyBgNumber('invalidate')}>Mark invalid</button>}
                </>
              )}
              {g.bgNumberStatus === 'INVALID' && (
                <>
                  <span className="bk-pill bk-pill--bad">Invalid</span>
                  <span className="mp-mono">{g.bgNumber}</span>
                </>
              )}
              {g.bgNumberStatus === 'EXPIRED' && (() => {
                const { remaining, blocked } = bgExpiry(g);
                return (
                  <>
                    <span className={`bk-pill bk-pill--${blocked ? 'bad' : 'wait'}`}>Expired</span>
                    <span className="mp-mono">{g.bgNumber}</span>
                    {remaining != null && (
                      <span className={blocked ? 'mp-danger' : 'bk-muted'}>
                        {blocked ? 'Bookings blocked' : `${remaining} ${remaining === 1 ? 'day' : 'days'} left in grace`}
                      </span>
                    )}
                  </>
                );
              })()}
              {!g.bgNumber && <span className="bk-pill bk-pill--bad">Not provided</span>}
              {editable && fieldButton(g.bgNumber ? 'Edit BG number' : 'Set BG number', () => { setBgInput(g.bgNumber || ''); setEditing('bg'); }, g.bgNumber ? 'Edit' : 'Set')}
            </span>
          </li>
        )}
        {bgError && <li className="bk-form-error" role="alert">{bgError}</li>}
        {/* Approval for session types that need it (DMT, say) */}
        <SessionTypeApprovals gymnast={g} editable={editable} onUpdated={onUpdated} itemStyle={INFO_ITEM_STYLE} keyStyle={KEY_STYLE} />
        <li style={{ ...INFO_ITEM_STYLE, ...(!g.isSelf ? { borderBottom: 'none' } : {}) }}>
          <span style={KEY_STYLE}>Health notes</span>
          <span className="mp-info__value">
            <span>
              {g.healthNotes === 'none'
                ? <span className="bk-muted">None</span>
                : g.healthNotes || <em className="bk-muted">Not recorded</em>}
            </span>
            {editable && fieldButton('Edit health notes', () => {
              setHealthNotesValue(g.healthNotes === 'none' ? '' : g.healthNotes || '');
              setHealthNotesNone(g.healthNotes === 'none');
              setEditing('health');
            })}
          </span>
        </li>
        {/* Emergency contact (adult participants only), in full */}
        {g.isSelf && (
          <li style={{ ...INFO_ITEM_STYLE, borderBottom: 'none' }}>
            <span style={KEY_STYLE}>Emergency contact</span>
            <span style={{ textAlign: 'right' }}>
              {g.emergencyContactName
                ? <>
                    <span style={{ fontWeight: 500 }}>{g.emergencyContactName}</span>
                    {g.emergencyContactRelationship && <span className="bk-muted"> ({g.emergencyContactRelationship})</span>}
                    {g.emergencyContactPhone && <><br /><a href={waLink(g.emergencyContactPhone)} target="_blank" rel="noopener noreferrer" className="bk-link">{g.emergencyContactPhone}</a></>}
                  </>
                : <span className="bk-pill bk-pill--bad">Missing</span>}
            </span>
          </li>
        )}
      </ul>

      <div className="mp-sub">
        <h4 className="mp-sub__title">Weekly slots</h4>
        {!membership && (
          <p className="bk-muted mp-small">Weekly slots need a membership. Use Manage membership on the Payments tab.</p>
        )}
        {membership && (
          <>
            {commitmentLoading && <Skeleton rows={2} label="Loading weekly slots…" />}
            {commitmentError && <p className="bk-form-error" role="alert">{commitmentError}</p>}
            {commitments !== null && (
              <>
                {commitments.length === 0 && <p className="bk-muted mp-small">No standing slots.</p>}
                {commitments.map(c => {
                  const label = `${DAYS[c.template.dayOfWeek]} ${c.template.startTime} to ${c.template.endTime}`;
                  const isFuture = c.status === 'ACTIVE' && c.startDate && new Date(c.startDate) > new Date();
                  return (
                    <div key={c.id} className="mp-slot">
                      <span className="mp-slot__label">
                        {label}
                        {isFuture
                          ? <span className="bk-pill bk-pill--info">Starts {formatDate(c.startDate)}</span>
                          : (
                            <span className={`bk-pill bk-pill--${c.status === 'ACTIVE' ? 'good' : c.status === 'WAITLISTED' ? 'wait' : 'off'}`}>
                              {c.status === 'ACTIVE' ? 'Active' : c.status === 'WAITLISTED' ? 'Waitlisted' : 'Paused'}
                            </span>
                          )}
                      </span>
                      {editable && (
                        <span className="bk-row-actions">
                          {c.status !== 'WAITLISTED' && !isFuture && (
                            <button type="button" className="bk-btn bk-btn--sm" onClick={() => handleToggleCommitmentStatus(c)}>
                              {c.status === 'ACTIVE' ? 'Pause' : 'Resume'}
                            </button>
                          )}
                          <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger" onClick={() => handleDeleteCommitment(c, label)}>
                            Remove
                          </button>
                        </span>
                      )}
                    </div>
                  );
                })}
                {editable && (
                  <div className="mp-add-slot">
                    <label className="bk-label">Add a standing slot
                      <select value={addingTemplateId} onChange={e => setAddingTemplateId(e.target.value)} className="bk-input">
                        <option value="">Choose a session</option>
                        {(templates || []).filter(t => t.isActive && !commitments.some(c => c.templateId === t.id)).map(t => (
                          <option key={t.id} value={t.id}>{DAYS[t.dayOfWeek]} {t.startTime} to {t.endTime} · {typeName(t)}</option>
                        ))}
                      </select>
                    </label>
                    <label className="bk-label">From
                      <input type="date" className="bk-input" value={addingStartDate} onChange={e => setAddingStartDate(e.target.value)} />
                    </label>
                    <button type="button" className="bk-btn bk-btn--sm bk-btn--primary" disabled={!addingTemplateId} onClick={handleAddCommitment}>
                      Add slot
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Guardians: other accounts linked to this child (the parent whose
          profile this is isn't repeated) */}
      {!g.isSelf && (editMode || otherGuardians.length > 0) && (
        <div className="mp-sub">
          <div className="mp-sub__head">
            <h4 className="mp-sub__title">Linked accounts</h4>
            {editable && (
              <button type="button" className="bk-btn bk-btn--sm" onClick={() => setEditing('link')}>Link account</button>
            )}
          </div>
          {otherGuardians.length === 0 && <p className="bk-muted mp-small">No other linked accounts.</p>}
          {otherGuardians.map(gu => (
            <div key={gu.id} className="mp-guardian">
              <span>{gu.firstName} {gu.lastName}</span>
              {editable && (
                <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger" disabled={removingGuardianId === gu.id}
                  onClick={() => handleRemoveGuardian(gu.id)}>
                  {removingGuardianId === gu.id ? 'Unlinking…' : 'Unlink'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Remove or unlink */}
      {editable && (
        <div className="mp-sub">
          {g.isSelf
            ? <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger" onClick={() => setEditing('unlink')}>Unlink self as gymnast</button>
            : <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger" onClick={removeChild}>Remove child</button>}
        </div>
      )}

      {editing === 'name' && (
        <FormSheet title={`Edit ${g.firstName}'s name`} onSave={saveName} onClose={close}
          dirty={nameForm.firstName !== g.firstName || nameForm.lastName !== g.lastName}>
          <div className="bk-grid-2">
            <label className="bk-label">First name
              <input className="bk-input" value={nameForm.firstName} required onChange={e => setNameForm(f => ({ ...f, firstName: e.target.value }))} />
            </label>
            <label className="bk-label">Last name
              <input className="bk-input" value={nameForm.lastName} required onChange={e => setNameForm(f => ({ ...f, lastName: e.target.value }))} />
            </label>
          </div>
        </FormSheet>
      )}
      {editing === 'dob' && (
        <FormSheet title={`${g.firstName}'s date of birth`} onSave={saveDob} onClose={close}
          dirty={dobValue !== (g.dateOfBirth ? new Date(g.dateOfBirth).toISOString().slice(0, 10) : '')}>
          <label className="bk-label">Date of birth
            <input type="date" className="bk-input" value={dobValue} required onChange={e => setDobValue(e.target.value)} />
          </label>
        </FormSheet>
      )}
      {editing === 'bg' && (
        <FormSheet title={`${g.firstName}'s BG number`} onSave={saveBgNumber} onClose={close} dirty={bgInput !== (g.bgNumber || '')}>
          <label className="bk-label">BG number
            <input className="bk-input" value={bgInput} required autoComplete="off" onChange={e => setBgInput(e.target.value)} />
          </label>
        </FormSheet>
      )}
      {editing === 'health' && (
        <FormSheet title={`${g.firstName}'s health notes`} onSave={saveHealthNotes} onClose={close}
          dirty={healthNotesNone !== (g.healthNotes === 'none') || healthNotesValue !== (g.healthNotes === 'none' ? '' : g.healthNotes || '')}>
          <label className="mp-check">
            <input type="checkbox" checked={healthNotesNone}
              onChange={e => { setHealthNotesNone(e.target.checked); if (e.target.checked) setHealthNotesValue(''); }} />
            No known health issues
          </label>
          {!healthNotesNone && (
            <label className="bk-label">Health issues or learning differences
              <textarea className="bk-input" rows={3} value={healthNotesValue} onChange={e => setHealthNotesValue(e.target.value)}
                placeholder="Describe any health issues or learning differences" />
            </label>
          )}
        </FormSheet>
      )}
      {editing === 'link' && (
        <LinkAccountSheet gymnast={g} allUsers={allUsers} onClose={close} onDone={() => { setEditing(null); onUpdated(); }} />
      )}
      {editing === 'unlink' && (
        <UnlinkSelfSheet gymnast={g} onClose={close}
          onDone={(note) => {
            setEditing(null);
            toast(note ? `${g.firstName} ${g.lastName} archived.${note}` : `${g.firstName} ${g.lastName} unlinked`);
            onUpdated();
          }} />
      )}
    </div>
  );
}


// Member profile pieces

const MEMBERSHIP_LABEL = {
  ACTIVE: 'Active', PAUSED: 'Paused', PENDING_PAYMENT: 'Awaiting payment', SCHEDULED: 'Starts soon',
};

const ageFrom = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  if (now < new Date(now.getFullYear(), d.getMonth(), d.getDate())) years -= 1;
  return years;
};

// Only problems are shown; a verified BG number needs no pill.
const BG_PILL = {
  PENDING:   { label: 'BG pending', tone: 'wait' },
  INVALID:   { label: 'BG invalid', tone: 'bad' },
  EXPIRED:   { label: 'BG expired', tone: 'bad' },
  NOT_ON_BG: { label: 'Not on BG', tone: 'wait' },
};

// Status shown on a collapsed child card, so problems are visible without
// opening it. Children with a parent always have the parent as emergency
// contact; only an adult training themselves can have nobody listed.
function childPills(g, membership, has = () => true) {
  const pills = [];
  const bgRequired = has('bgBlocking') && ((g.pastSessionCount ?? 0) >= 2 || !!membership);
  if (!has('bgNumbers')) { /* this club doesn't record BG numbers */ }
  else if (g.bgNumberStatus && BG_PILL[g.bgNumberStatus]) pills.push(BG_PILL[g.bgNumberStatus]);
  else if (g.bgNumberStatus !== 'VERIFIED' && !g.bgNumber && bgRequired) pills.push({ label: 'No BG number', tone: 'bad' });
  if (!g.dateOfBirth) pills.push({ label: 'No date of birth', tone: 'wait' });
  if (g.isSelf && !g.emergencyContactName) pills.push({ label: 'No emergency contact', tone: 'bad' });
  return pills;
}

function ChildCard({ g, membership, open, onToggle, children }) {
  const age = ageFrom(g.dateOfBirth);
  const has = useFeatures();
  return (
    <div className={`mp-child${open ? ' open' : ''}`}>
      <button type="button" className="mp-child__head" aria-expanded={!!open} onClick={onToggle}>
        <span className="mp-child__name">{g.firstName} {g.lastName}</span>
        {age != null && <span className="bk-pill bk-pill--off">Age {age}</span>}
        {childPills(g, membership, has).map(p => (
          <span key={p.label} className={`bk-pill bk-pill--${p.tone}`}>{p.label}</span>
        ))}
        <Chevron />
      </button>
      {open && <div className="mp-child__body">{children}</div>}
    </div>
  );
}

// The more-actions menu in the profile header. Closes on selection, outside tap or Escape.
function ProfileMenu({ items }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <span className="mp-menu" ref={ref}>
      <button type="button" className="ui-icon-btn mp-menu__toggle" aria-haspopup="menu" aria-expanded={open}
        aria-label="Member actions" onClick={() => setOpen(o => !o)}>
        <EllipsisHorizontalIcon aria-hidden="true" />
      </button>
      {open && (
        <span className="mp-menu__list" role="menu">
          {items.map(item => (
            <button key={item.label} type="button" role="menuitem"
              className={`mp-menu__item${item.danger ? ' mp-menu__item--danger' : ''}`}
              onClick={() => { setOpen(false); item.onSelect(); }}>
              {item.label}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

const BACK = { to: '/booking/admin/members', label: 'Members' };

function MemberDetail({ userId, onRemoved, allUsers, focusChildId = null }) {
  const [member, setMember] = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(null); // 'details' | 'child' | 'credit' | 'charge'
  const [editingRole, setEditingRole] = useState(false);
  const [error, setError] = useState(null);
  const [accountError, setAccountError] = useState(null);
  const [sendingReset, setSendingReset] = useState(false);
  const [memberCharges, setMemberCharges] = useState(null); // null = not yet fetched
  const [chargesLoading, setChargesLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const confirm = useConfirm();
  const toast = useToast();
  // Archive, delete and role changes are Club admin only on the server too;
  // money follows payments access
  const { isClubAdmin, can } = useAuth();
  const seesPayments = can('payments');
  const managesPayments = can('payments', 'MANAGE');
  const [tab, setTab] = useUrlTab(seesPayments ? ['children', 'payments', 'account'] : ['children', 'account']);
  const [openChildren, setOpenChildren] = useState(null); // Set of gymnast ids; null until first load
  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);

  useEffect(() => { getTemplates().then(r => setTemplates(r.data)).catch(() => {}); }, []);

  // The club's roles, to name the roles someone holds and tick them (Club admins only)
  const [clubRoles, setClubRoles] = useState(null);
  useEffect(() => {
    if (!isClubAdmin) return;
    bookingApi.getRoles().then(r => setClubRoles(Array.isArray(r.data) ? r.data : null)).catch(() => {});
  }, [isClubAdmin]);

  const load = () => {
    setLoading(prev => prev && !member);
    return Promise.all([
      bookingApi.getMember(userId),
      bookingApi.getMemberships().catch(() => ({ data: [] })),
    ]).then(([mRes, memRes]) => {
      setMember(mRes.data);
      setMemberships(Array.isArray(memRes.data) ? memRes.data : []);
    }).catch(err => { console.error(err); })
      .finally(() => setLoading(false));
  };

  const loadCharges = async () => {
    setChargesLoading(true);
    try {
      const res = await bookingApi.getChargesForUser(userId);
      setMemberCharges(res.data.filter(c => !c.paidAt)); // outstanding only
    } catch {
      setMemberCharges([]);
    } finally {
      setChargesLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    setSendingReset(true);
    setAccountError(null);
    try {
      const res = await bookingApi.resetPassword(userId);
      toast(res.data.message || 'Password reset email sent');
    } catch (err) {
      setAccountError(errorText(err, "Couldn't send the reset email."));
    } finally {
      setSendingReset(false);
    }
  };

  const handleRemoveMember = async () => {
    const ok = await confirm({
      title: `Delete ${member.firstName} and all their children?`,
      message: 'This cannot be undone. Only a summary is kept.',
      confirmLabel: 'Delete member',
    });
    if (!ok) return;
    setAccountError(null);
    try {
      await bookingApi.deleteMember(userId);
      toast(`${member.firstName} ${member.lastName} deleted`);
      onRemoved?.();
    } catch (err) {
      setAccountError(errorText(err, "Couldn't delete the member."));
    }
  };

  const handleArchive = async () => {
    setArchiving(true);
    setAccountError(null);
    try {
      await bookingApi.archiveMember(userId, archiveReason.trim());
      setArchiveReason('');
      toast(`${member.firstName} ${member.lastName} archived`);
      load();
    } catch (err) {
      setAccountError(errorText(err, "Couldn't archive the member."));
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async () => {
    setArchiving(true);
    setAccountError(null);
    setError(null);
    try {
      await bookingApi.restoreMember(userId);
      toast(`${member.firstName} ${member.lastName} restored`);
      load();
    } catch (err) {
      const message = errorText(err, "Couldn't restore the member.");
      setAccountError(message);
      setError(message);
    } finally {
      setArchiving(false);
    }
  };

  const handleDeleteCredit = async (c) => {
    const ok = await confirm({
      title: `Delete ${money(c.amount)} of credit?`,
      message: `It comes off ${member.firstName}'s account straight away.`,
      confirmLabel: 'Delete credit',
      tone: 'primary',
    });
    if (!ok) return;
    setError(null);
    try {
      await bookingApi.deleteCredit(c.id);
      toast('Credit deleted');
      await load();
    } catch (err) {
      setError(errorText(err, "Couldn't delete the credit."));
    }
  };

  const handleDeleteCharge = async (c) => {
    const ok = await confirm({
      title: `Delete the ${money(c.amount)} charge?`,
      message: `${c.description} will no longer be owed.`,
      confirmLabel: 'Delete charge',
      tone: 'primary',
    });
    if (!ok) return;
    setError(null);
    try {
      await bookingApi.deleteCharge(c.id);
      toast('Charge deleted');
      await loadCharges();
    } catch (err) {
      setError(errorText(err, "Couldn't delete the charge."));
    }
  };

  const handleMarkAsGymnast = async (id) => {
    const ok = await confirm({
      title: 'Mark them as a gymnast?',
      message: 'This creates a participant record for this adult member, so they can book sessions for themselves.',
      confirmLabel: 'Mark as gymnast',
      tone: 'primary',
    });
    if (!ok) return;
    setError(null);
    try {
      try {
        await bookingApi.markAdultAsGymnast(id);
      } catch (err) {
        const choice = askAboutExistingRecord(err, 'their');
        if (!choice) throw err;
        await bookingApi.markAdultAsGymnast(id, choice);
      }
      toast('Marked as a gymnast');
      load();
    } catch (err) {
      setError(errorText(err, "Couldn't create the participant record."));
    }
  };

  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charges load the first time the Payments tab is opened
  useEffect(() => {
    if (tab === 'payments' && seesPayments && memberCharges === null && !chargesLoading) loadCharges();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <><PageHeader back={BACK} title="Member" /><Skeleton rows={6} label="Loading member…" /></>;
  }
  if (!member) {
    return (
      <>
        <PageHeader back={BACK} title="Member" />
        <ErrorState title="Couldn't load this member" onRetry={() => { setLoading(true); load(); }} />
      </>
    );
  }

  const totalCredits = member.credits.reduce((s, c) => s + c.amount, 0);
  const outstandingChargesTotal = memberCharges ? memberCharges.reduce((s, c) => s + c.amount, 0) : 0;
  const gymnasts = [...member.gymnasts].sort((a, b) => (b.isSelf ? 1 : 0) - (a.isSelf ? 1 : 0));
  // The Memberships page searches by gymnast name: use the children's shared
  // surname so it finds all of them, or the first child's full name.
  const surnames = [...new Set(gymnasts.map(g => g.lastName))];
  const membershipSearch = surnames.length === 1 ? surnames[0] : (gymnasts[0] ? `${gymnasts[0].firstName} ${gymnasts[0].lastName}` : '');
  const membershipFor = (g) => memberships.find(m => m.gymnastId === g.id && m.status !== 'CANCELLED') ?? null;
  const activeMemberships = gymnasts.map(membershipFor).filter(m => m && m.status === 'ACTIVE');
  const monthlyTotal = activeMemberships.reduce((sum, m) => sum + m.monthlyAmount, 0);

  // Until a card is toggled, the child the list search pointed at (or the
  // first one) is open
  const firstOpen = focusChildId && member.gymnasts.some(g => g.id === focusChildId)
    ? focusChildId
    : member.gymnasts[0]?.id;
  const isOpen = (id) => (openChildren ? openChildren.has(id) : id === firstOpen);
  const toggleChild = (id) => setOpenChildren(prev => {
    const next = new Set(prev || (firstOpen ? [firstOpen] : []));
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const menuItems = [
    // Editing a member's details is for Club admins only on the server
    ...(isClubAdmin ? [{ label: 'Edit details', onSelect: () => setSheet('details') }] : []),
    { label: 'Add a child', onSelect: () => { setTab('children'); setSheet('child'); } },
    ...(!member.gymnasts.some(g => g.isSelf)
      ? [{ label: 'Mark as gymnast', onSelect: () => handleMarkAsGymnast(member.id) }]
      : []),
    // Money follows payments access
    ...(managesPayments ? [
      { label: 'Assign credit', onSelect: () => { setTab('payments'); setSheet('credit'); } },
      { label: 'Add charge', onSelect: () => { setTab('payments'); setSheet('charge'); } },
    ] : []),
    // Archiving and deleting are for Club admins only
    ...(isClubAdmin ? [
      member.isArchived
        ? { label: 'Restore member', onSelect: handleRestore }
        : { label: 'Archive member', onSelect: () => setTab('account') },
      { label: 'Delete member', onSelect: () => { setTab('account'); handleRemoveMember(); }, danger: true },
    ] : []),
  ];

  const tabs = [
    { id: 'children', label: `Participants (${gymnasts.length})` },
    ...(seesPayments ? [{ id: 'payments', label: 'Payments' }] : []),
    { id: 'account', label: 'Account' },
  ];

  return (
    <div className="mp">
      <PageHeader back={BACK} title={`${member.firstName} ${member.lastName}`} />

      {member.isArchived && (
        <div className="mp-banner">
          <span>
            <strong>Archived</strong>
            {member.archivedAt ? ` ${formatDate(member.archivedAt)}` : ''}
            {member.archivedReason ? ` · ${member.archivedReason}` : ''}
            <span className="mp-banner__note">They'll be restored automatically if they log in.</span>
          </span>
          {isClubAdmin && <button type="button" className="bk-btn bk-btn--sm" onClick={handleRestore} disabled={archiving}>Restore</button>}
        </div>
      )}

      {/* Role, contact and actions */}
      <div className="mp-hero">
        <div className="mp-hero__top">
          <span className="bk-pill bk-pill--info">{roleLabel(member, clubRoles)}</span>
          {member.role === 'ADULT' && !member.isArchived && <MessageFamilyButton userId={member.id} className="mp-mini" />}
          <ProfileMenu items={menuItems} />
        </div>
        <div className="mp-contact">
          <div className="mp-contact__line">
            <span className="mp-contact__value">
              <span className="mp-contact__label">Phone</span>
              {member.phone || <span className="mp-danger">No phone number</span>}
            </span>
            {member.phone && (
              <span className="mp-contact__actions">
                <a className="mp-mini" href={`tel:${member.phone.replace(/[^\d+]/g, '')}`}>Call</a>
                <a className="mp-mini" href={waLink(member.phone)} target="_blank" rel="noopener noreferrer">WhatsApp</a>
              </span>
            )}
          </div>
          <div className="mp-contact__line">
            <span className="mp-contact__value">
              <span className="mp-contact__label">Email</span>
              {member.email}
            </span>
            <a className="mp-mini" href={`mailto:${member.email}`}>Email</a>
          </div>
        </div>
      </div>

      <Tabs tabs={tabs} value={tab} onChange={setTab} label="Profile sections" />

      {error && <p className="bk-form-error" role="alert">{error}</p>}

      {/* Participants */}
      {tab === 'children' && (
        <div className="mp-panel">
          {gymnasts.length === 0 && <p className="bk-muted mp-flush">No participants linked to this account. Use Member actions to add a child.</p>}
          {gymnasts.map(g => (
            <ChildCard key={g.id} g={g} membership={membershipFor(g)} open={isOpen(g.id)} onToggle={() => toggleChild(g.id)}>
              <GymnastRow g={g} memberships={memberships} templates={templates} onUpdated={load} allUsers={allUsers} profileUserId={member.id} />
            </ChildCard>
          ))}
        </div>
      )}

      {/* Payments */}
      {tab === 'payments' && seesPayments && (
        <div className="mp-panel">
          <section className="mp-block">
            <div className="mp-block__head">
              <h3 className="mp-block__title">Memberships</h3>
              <Link className="bk-btn bk-btn--sm" to={`/booking/admin/memberships${membershipSearch ? `?search=${encodeURIComponent(membershipSearch)}` : ''}`}>
                Manage membership
              </Link>
            </div>
            {gymnasts.length === 0 && <p className="bk-muted mp-flush">No children yet.</p>}
            {gymnasts.map(g => {
              const m = membershipFor(g);
              if (!m) {
                return (
                  <div key={g.id} className="mp-line mp-line--row">
                    <span>{g.firstName}</span>
                    <span className="bk-muted">Pays per session</span>
                  </div>
                );
              }
              // Opens this membership, expanded, on the Memberships page
              return (
                <Link key={g.id} className="mp-line mp-line--row mp-line--link" to={`/booking/admin/memberships?membership=${m.id}`}>
                  <span>{g.firstName}</span>
                  <span className="mp-line__end">
                    <span className={`bk-pill ${m.status === 'ACTIVE' ? 'bk-pill--good' : 'bk-pill--wait'}`}>
                      {MEMBERSHIP_LABEL[m.status] || m.status}{m.monthlyAmount ? ` · ${money(m.monthlyAmount)} a month` : ''}
                    </span>
                    <ChevronRightIcon className="mp-line__chev" aria-hidden="true" />
                  </span>
                </Link>
              );
            })}
            {activeMemberships.length > 1 && (
              <div className="mp-total"><span>Total each month</span><strong>{money(monthlyTotal)}</strong></div>
            )}
          </section>

          <section className="mp-block">
            <div className="mp-block__head">
              <h3 className="mp-block__title">Credit</h3>
              {managesPayments && (
                <button type="button" className="bk-btn bk-btn--sm bk-btn--primary" onClick={() => setSheet('credit')}>Assign credit</button>
              )}
            </div>
            {member.credits.length === 0 && <p className="bk-muted mp-flush">No credit.</p>}
            {member.credits.map(c => (
              <div key={c.id} className="mp-line mp-line--row">
                <span>{money(c.amount)}</span>
                <span className="bk-muted">Expires {formatDate(c.expiresAt)}</span>
                {managesPayments && !c.usedOnBookingId && (
                  <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger" onClick={() => handleDeleteCredit(c)}>Delete</button>
                )}
              </div>
            ))}
            {member.credits.length > 0 && (
              <div className="mp-total"><span>Total credit</span><strong>{money(totalCredits)}</strong></div>
            )}
          </section>

          <section className="mp-block">
            <div className="mp-block__head">
              <h3 className="mp-block__title">Outstanding charges</h3>
              {managesPayments && (
                <button type="button" className="bk-btn bk-btn--sm bk-btn--primary" onClick={() => setSheet('charge')}>Add charge</button>
              )}
            </div>
            {chargesLoading && <Skeleton rows={2} label="Loading charges…" />}
            {!chargesLoading && memberCharges !== null && (
              <>
                {memberCharges.length === 0 && <p className="bk-muted mp-flush">No outstanding charges.</p>}
                {memberCharges.map(c => (
                  <div key={c.id} className="mp-line mp-line--row">
                    <span>{c.description}</span>
                    <span className="bk-muted">{money(c.amount)} · Due {formatDate(c.dueDate)}</span>
                    {managesPayments && (
                      <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger" onClick={() => handleDeleteCharge(c)}>Delete</button>
                    )}
                  </div>
                ))}
                {memberCharges.length > 0 && (
                  <div className="mp-total"><span>Total outstanding</span><strong>{money(outstandingChargesTotal)}</strong></div>
                )}
              </>
            )}
          </section>
        </div>
      )}

      {/* Account */}
      {tab === 'account' && (
        <div className="mp-panel">
          {accountError && <p className="bk-form-error" role="alert">{accountError}</p>}
          <section className="mp-block">
            <h3 className="mp-block__title">{member.role === 'GYMNAST' ? 'Role' : 'Roles'}</h3>
            {isClubAdmin && member.role !== 'GYMNAST' ? (
              <StaffRoles member={member} roles={clubRoles || undefined}
                onSaved={(saved) => setMember(m => ({ ...m, role: saved?.role ?? m.role, roleIds: saved?.roleIds ?? m.roleIds }))} />
            ) : (
              <>
                <div className="mp-line">
                  <span className="bk-pill bk-pill--info">{roleLabel(member, clubRoles)}</span>
                  {isClubAdmin && !editingRole && <button type="button" className="bk-btn bk-btn--sm" onClick={() => setEditingRole(true)}>Change role</button>}
                </div>
                {editingRole && (
                  <RoleSelector member={member} onCancel={() => setEditingRole(false)}
                    onDone={() => { setEditingRole(false); toast('Role changed'); load(); }} />
                )}
              </>
            )}
          </section>

          <section className="mp-block">
            <h3 className="mp-block__title">Login</h3>
            <div className="mp-line">
              <span className="bk-muted">
                Member since {formatMonth(member.createdAt)}
                {' · '}
                {member.lastLoginAt ? `Last signed in ${formatDate(member.lastLoginAt)}` : 'Has never signed in'}
              </span>
            </div>
            {/* Club admins only, as on the server */}
            {isClubAdmin && (
              <button type="button" className="bk-btn bk-btn--sm mp-self-start" disabled={sendingReset} onClick={handlePasswordReset}>
                {sendingReset ? 'Sending…' : 'Send password reset'}
              </button>
            )}
          </section>

          {isClubAdmin && (<>
          <section className="mp-block">
            <h3 className="mp-block__title">{member.isArchived ? 'Archived' : 'Archive'}</h3>
            {member.isArchived ? (
              <>
                <span className="bk-muted">
                  Archived{member.archivedAt ? ` ${formatDate(member.archivedAt)}` : ''}{member.archivedReason ? ` · ${member.archivedReason}` : ''}.
                  Everything is kept, and they're restored automatically if they log in.
                </span>
                <button type="button" className="bk-btn bk-btn--sm bk-btn--primary mp-self-start" disabled={archiving} onClick={handleRestore}>
                  {archiving ? 'Restoring…' : 'Restore member'}
                </button>
              </>
            ) : (
              <>
                <span className="bk-muted">
                  For families who've left. Hides them from the member list and keeps all their history, payments and
                  children's progress. They're restored automatically if they log in.
                </span>
                {gymnasts.filter(g => membershipFor(g)?.status === 'ACTIVE').map(g => (
                  <p key={g.id} className="mp-warning">
                    {g.firstName} still has an active membership at {money(membershipFor(g).monthlyAmount)} a month.
                    Archiving won't stop it; cancel it on the Payments tab if they've left.
                  </p>
                ))}
                <label className="bk-label">Reason <span className="bk-optional">(optional)</span>
                  <input className="bk-input" placeholder="For example, moved away" value={archiveReason}
                    onChange={e => setArchiveReason(e.target.value)} aria-label="Reason for archiving" maxLength={200} />
                </label>
                <button type="button" className="bk-btn bk-btn--sm mp-self-start" disabled={archiving} onClick={handleArchive}>
                  {archiving ? 'Archiving…' : 'Archive member'}
                </button>
              </>
            )}
          </section>

          <section className="mp-block">
            <h3 className="mp-block__title">Delete permanently</h3>
            <span className="bk-muted">
              Deletes {member.firstName} and all their children for good. Only a summary is kept. Use Archive instead unless
              they've asked to be removed.
            </span>
            <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger mp-self-start" onClick={handleRemoveMember}>
              Delete member
            </button>
          </section>
          </>)}
        </div>
      )}

      {sheet === 'details' && (
        <EditProfileSheet member={member} onClose={() => setSheet(null)}
          onDone={() => { setSheet(null); toast('Details saved'); load(); }} />
      )}
      {sheet === 'child' && (
        <AddChildSheet userId={userId} onClose={() => setSheet(null)}
          onDone={(name) => { setSheet(null); toast(`${name} added`); load(); }} />
      )}
      {sheet === 'credit' && (
        <AssignCreditSheet userId={member.id} onClose={() => setSheet(null)}
          onDone={(amount) => { setSheet(null); toast(`${money(amount)} credit assigned`); load(); }} />
      )}
      {sheet === 'charge' && (
        <AddChargeSheet userId={userId} onClose={() => setSheet(null)}
          onDone={() => { setSheet(null); toast('Charge added'); loadCharges(); }} />
      )}
    </div>
  );
}

// Page: /booking/admin/members/:userId
export default function AdminMemberProfile() {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);

  // Other members, for linking a second parent to a child
  useEffect(() => {
    bookingApi.getMembers()
      .then(r => setAllUsers((Array.isArray(r.data) ? r.data : r.data.users || []).filter(u => !u.isGymnast)))
      .catch(() => {});
  }, []);

  return (
    <div className="bk-page bk-page--narrow mp-page">
      <MemberDetail
        key={userId}
        userId={userId}
        allUsers={allUsers}
        focusChildId={searchParams.get('child')}
        onRemoved={() => navigate('/booking/admin/members', { replace: true })}
      />
    </div>
  );
}
